import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useSubscribeToFavorite } from '@jira/platform__favouriting-favourite-change-provider/src/common/utils/favorite-pub-sub/index.tsx';
import type { FavoriteSubscriptionPayload } from '@jira/platform__favouriting-favourite-change-provider/src/common/utils/favorite-pub-sub/types.tsx';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { useSyncChangeFavouritesMutation } from './__generated__/useSyncChangeFavouritesMutation.graphql';

/*
Hook to trigger a mutation to reorder the projects in the sidebar when a project is starred.
This is used to ensure that the newly starred project is placed at the top of the custom order.
*/
export function useSyncChangeFavourites(): void {
	const cloudId = useCloudId();

	const [commitMutation] = useMutation<useSyncChangeFavouritesMutation>(graphql`
		mutation useSyncChangeFavouritesMutation($input: JiraReorderSidebarMenuItemInput!) {
			jira_reorderProjectsSidebarMenuItem(input: $input) @optIn(to: "JiraSidebarMenu") {
				id
				favourites(first: 15) {
					__id
					pageInfo {
						hasNextPage
					}
					edges {
						node {
							id
							projectKey: key
							...Nav4ProjectMenuItem
						}
					}
				}
			}
		}
	`);

	const moveFavouriteToTop = useCallback(
		(payload: FavoriteSubscriptionPayload) => {
			// Prevent optimistic updates from triggering setting custom order
			if (payload.type === 'MUTATION' && payload.isOptimistic) {
				return;
			}
			if (
				payload.isFavourite &&
				(expVal('fix_error_when_starring_project_from_jfy_page', 'isEnabled', false)
					? JiraProjectAri.check(payload.entityId)
					: true)
			) {
				const itemId = JiraProjectAri.parse(payload.entityId).projectId;
				commitMutation({
					variables: {
						input: {
							cloudId,
							reorderMode: 'MOVE_TO_TOP',
							menuItem: {
								// eslint-disable-next-line jira/relay/mutation-variables-from-relay-data
								itemId,
							},
						},
					},
				});
			}
		},
		[cloudId, commitMutation],
	);

	useSubscribeToFavorite('JiraProject', moveFavouriteToTop);
}
